/**
 * features/admissions/AdmissionFormTextEditor.tsx
 * ------------------------------------------------------------------
 * Admin editor for the §7 boilerplate printed on the application form:
 * the fee statement, the Documents-Required list, the Documents-Received
 * office checklist, and the Conditions of Admission.
 *
 * Stored at Firestore `reportSchoolSettings/admissionFormText` (staff
 * read, admin write — same gate as the admission-window doc). The print
 * PDF (applicationFormPdf.ts) reads this doc and falls back to
 * lib/admissionFormDefaults when it is missing/empty.
 *
 * Rendered inside the Admissions → Settings tab. Every list supports
 * add / remove / reorder / edit.
 * ------------------------------------------------------------------
 */
import { useEffect, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { setDoc } from '@/lib/firebase';
import {
  FileText,
  Save,
  Plus,
  Trash2,
  ArrowUp,
  ArrowDown,
  AlertTriangle,
  RotateCcw,
  Loader2,
  CheckCircle2,
  AlertCircle,
} from 'lucide-react';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { SoftCard, AccentButton } from '@/features/finance/ui';
import { Spinner } from '@/components/ui/primitives';
import {
  DEFAULT_ADMISSION_FORM_TEXT,
  resolveAdmissionFormText,
} from '@/lib/admissionFormDefaults';
import type { AdmissionFormText } from '@/types';

const DOC_PATH = ['reportSchoolSettings', 'admissionFormText'] as const;

export default function AdmissionFormTextEditor() {
  const { userData } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const [feeStatement, setFeeStatement] = useState('');
  const [documentsRequired, setDocumentsRequired] = useState<string[]>([]);
  const [documentsReceived, setDocumentsReceived] = useState<string[]>([]);
  const [conditions, setConditions] = useState<string[]>([]);
  const [original, setOriginal] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const snap = await getDoc(doc(db, ...DOC_PATH));
        const resolved = resolveAdmissionFormText(
          snap.exists() ? (snap.data() as AdmissionFormText) : null,
        );
        applyState(resolved);
      } catch (e) {
        setMsg({ ok: false, text: (e as Error).message });
        applyState(DEFAULT_ADMISSION_FORM_TEXT);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function applyState(t: AdmissionFormText) {
    setFeeStatement(t.feeStatement);
    setDocumentsRequired(t.documentsRequired);
    setDocumentsReceived(t.documentsReceived);
    setConditions(t.conditions);
    setOriginal(snapshot(t.feeStatement, t.documentsRequired, t.documentsReceived, t.conditions));
  }

  function snapshot(fee: string, req: string[], rec: string[], cond: string[]) {
    return JSON.stringify({ fee, req, rec, cond });
  }

  const current = snapshot(feeStatement, documentsRequired, documentsReceived, conditions);
  const dirty = current !== original;

  async function handleSave() {
    setSaving(true);
    setMsg(null);
    try {
      // Drop blank rows on save.
      const clean = (arr: string[]) => arr.map((s) => s.trim()).filter(Boolean);
      const payload: AdmissionFormText = {
        id: 'admissionFormText',
        feeStatement: feeStatement.trim(),
        documentsRequired: clean(documentsRequired),
        documentsReceived: clean(documentsReceived),
        conditions: clean(conditions),
        updatedAt: new Date().toISOString(),
        updatedBy: userData?.uid ?? '',
      };
      await setDoc(doc(db, ...DOC_PATH), payload, { merge: true });
      applyState(resolveAdmissionFormText(payload));
      setMsg({ ok: true, text: 'Application-form text saved.' });
      setTimeout(() => setMsg(null), 2500);
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message });
    } finally {
      setSaving(false);
    }
  }

  function resetDefaults() {
    applyStateKeepDirty(DEFAULT_ADMISSION_FORM_TEXT);
  }
  /** Load defaults into the editors WITHOUT clearing the dirty flag
   *  (so the admin still has to Save to persist). */
  function applyStateKeepDirty(t: AdmissionFormText) {
    setFeeStatement(t.feeStatement);
    setDocumentsRequired([...t.documentsRequired]);
    setDocumentsReceived([...t.documentsReceived]);
    setConditions([...t.conditions]);
  }

  if (loading) return <Spinner label="Loading form text…" />;

  return (
    <div className="space-y-6">
      <SoftCard>
        <h3 className="text-base font-semibold mb-1 flex items-center gap-2">
          <FileText size={16} className="text-primaryLight" />
          Application-form boilerplate
        </h3>
        <p className="text-sm text-gray-500">
          These appear on the <strong>printed application form</strong> (section 7). Edit them
          here — no code change needed.
        </p>
        <div className="mt-3 p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 flex items-start gap-2">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          <span>
            <strong>Draft text — verify against your official master copy.</strong> The
            Conditions of Admission were transcribed from the paper form; please check every
            clause (especially the photo-usage / consultation wording) and correct as needed.
          </span>
        </div>
      </SoftCard>

      {msg && (
        <div
          className={`p-3 rounded-lg border text-sm flex items-start gap-2 ${
            msg.ok
              ? 'bg-emerald-50 border-emerald-200 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
              : 'bg-red-50 border-red-200 text-red-700 dark:bg-red-900/30 dark:text-red-300'
          }`}
        >
          {msg.ok ? <CheckCircle2 size={16} className="mt-0.5" /> : <AlertCircle size={16} className="mt-0.5" />}
          <span>{msg.text}</span>
        </div>
      )}

      {/* Fee statement */}
      <SoftCard>
        <h4 className="text-sm font-semibold mb-2">Fee statement</h4>
        <textarea
          value={feeStatement}
          onChange={(e) => setFeeStatement(e.target.value)}
          rows={3}
          className="w-full px-3 py-2 rounded-lg border-2 border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white focus:outline-none focus:border-primaryLight text-sm"
          placeholder="Fee statement shown on the form…"
        />
      </SoftCard>

      <ListEditor
        title="Documents required"
        note="Numbered list of documents the applicant must bring."
        numbered
        items={documentsRequired}
        setItems={setDocumentsRequired}
        placeholder="e.g. Certified copy of birth certificate"
      />

      <ListEditor
        title="Documents received (office checklist)"
        note="Each row prints with a blank tick box for the office."
        items={documentsReceived}
        setItems={setDocumentsReceived}
        placeholder="e.g. Birth certificate"
      />

      <ListEditor
        title="Conditions of Admission"
        note="Numbered clauses printed in section 7 and agreed to by the parent/guardian."
        numbered
        items={conditions}
        setItems={setConditions}
        placeholder="Condition text…"
      />

      {/* Save bar */}
      <div className="flex items-center justify-between gap-3">
        <button
          onClick={resetDefaults}
          className="text-xs text-gray-500 hover:text-gray-800 dark:hover:text-gray-200 flex items-center gap-1"
          title="Load the built-in default text into the editors (does not save until you click Save)"
        >
          <RotateCcw size={12} /> Load defaults
        </button>
        <div className="flex items-center gap-3">
          {dirty && (
            <span className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
              <AlertCircle size={12} /> Unsaved changes
            </span>
          )}
          <AccentButton icon={saving ? Loader2 : Save} onClick={handleSave} disabled={saving || !dirty}>
            {saving ? 'Saving…' : 'Save form text'}
          </AccentButton>
        </div>
      </div>
    </div>
  );
}

/* ---------------- reusable string[] editor ---------------- */
function ListEditor({
  title,
  note,
  items,
  setItems,
  numbered,
  placeholder,
}: {
  title: string;
  note?: string;
  items: string[];
  setItems: React.Dispatch<React.SetStateAction<string[]>>;
  numbered?: boolean;
  placeholder?: string;
}) {
  const update = (i: number, val: string) =>
    setItems((prev) => prev.map((x, idx) => (idx === i ? val : x)));
  const remove = (i: number) => setItems((prev) => prev.filter((_, idx) => idx !== i));
  const move = (i: number, dir: -1 | 1) =>
    setItems((prev) => {
      const j = i + dir;
      if (j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  const add = () => setItems((prev) => [...prev, '']);

  return (
    <SoftCard>
      <div className="flex items-center justify-between mb-1">
        <h4 className="text-sm font-semibold">{title}</h4>
        <span className="text-xs text-gray-400">{items.length} item(s)</span>
      </div>
      {note && <p className="text-xs text-gray-500 mb-3">{note}</p>}
      <div className="space-y-2">
        {items.map((item, i) => (
          <div key={i} className="flex items-start gap-2">
            {numbered && (
              <span className="mt-2 text-xs text-gray-400 w-5 text-right shrink-0">{i + 1}.</span>
            )}
            <textarea
              value={item}
              onChange={(e) => update(i, e.target.value)}
              rows={2}
              placeholder={placeholder}
              className="flex-1 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white focus:outline-none focus:border-primaryLight text-sm"
            />
            <div className="flex flex-col gap-1 shrink-0">
              <button
                onClick={() => move(i, -1)}
                disabled={i === 0}
                className="p-1 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30"
                title="Move up"
              >
                <ArrowUp size={13} />
              </button>
              <button
                onClick={() => move(i, 1)}
                disabled={i === items.length - 1}
                className="p-1 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30"
                title="Move down"
              >
                <ArrowDown size={13} />
              </button>
              <button
                onClick={() => remove(i)}
                className="p-1 rounded text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30"
                title="Remove"
              >
                <Trash2 size={13} />
              </button>
            </div>
          </div>
        ))}
      </div>
      <button
        onClick={add}
        className="mt-3 flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200"
      >
        <Plus size={14} /> Add item
      </button>
    </SoftCard>
  );
}
